import { captureError } from "@outschool/platform";
import type { StripeConnectInstance } from "@stripe/connect-js";
import React from "react";

export enum StripeConnectRolloutPhase {
  Early = "early",
  Mid = "mid",
  Late = "late",
  Final = "final",
}

interface StripeConnectContextValue {
  registerInstance: (instance: StripeConnectInstance) => void;
  logoutAll: () => void;
  rolloutPhase: StripeConnectRolloutPhase;
}

const StripeConnectContext = React.createContext<
  StripeConnectContextValue | undefined
>(undefined);

export const StripeConnectProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const instancesRef = React.useRef<Set<StripeConnectInstance>>(new Set());

  // TODO: Replace with actual rollout phase
  // update the roll out dates in the consts file as well
  const rolloutPhase = StripeConnectRolloutPhase.Early;
  const registerInstance = React.useCallback(
    (instance: StripeConnectInstance) => {
      instancesRef.current.add(instance);
    },
    [],
  );

  const logoutAll = React.useCallback(() => {
    instancesRef.current.forEach(instance => {
      try {
        instance.logout();
      } catch (error) {
        captureError(error, {
          extra: {
            message: "Failed to logout Stripe Connect instance",
          },
        });
      }
    });
    instancesRef.current.clear();
  }, []);

  const value = React.useMemo(
    () => ({
      registerInstance,
      logoutAll,
      rolloutPhase,
    }),
    [registerInstance, logoutAll, rolloutPhase],
  );

  return (
    <StripeConnectContext.Provider value={value}>
      {children}
    </StripeConnectContext.Provider>
  );
};

export const useStripeConnectContext = (): StripeConnectContextValue => {
  const context = React.useContext(StripeConnectContext);
  if (!context) {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        "useStripeConnectContext must be used within StripeConnectProvider",
      );
    }
    // Return safe no-ops to prevent crashes
    return {
      registerInstance: () => {},
      logoutAll: () => {},
      rolloutPhase: StripeConnectRolloutPhase.Early,
    };
  }
  return context;
};
